import { unwrapResult } from '@reduxjs/toolkit'
import { Route } from '../../../config/router'
import { Checkout } from '@afterpaytouch/portal-api/types'
import { ConsumerAccountResponse, CrossRegionRedirection } from '../../../model'
import {
  AsyncAction,
  accountLookup,
  afterpayCheckoutLoginTokenSelector,
  cashCheckoutTokenSelector,
  checkPersistentLogin,
  CheckoutAppDispatch,
  oAuth2faHeaders,
} from '../../../state'
import { hasOptedOutRememberMe } from '../../../flow/remember-me/optOut'
import { determinePersistentLoginRoute, InitialiseNextRoute } from '../routing'

type LoadPersistentConsumerResult = ConsumerAccountResponse | Route.VERIFY | null

const isConsumerAccountResponse = (loadPersistentConsumerResult: LoadPersistentConsumerResult): loadPersistentConsumerResult is ConsumerAccountResponse => {
  return loadPersistentConsumerResult !== null && loadPersistentConsumerResult !== Route.VERIFY
}

const loadPersistentConsumer = (): AsyncAction<LoadPersistentConsumerResult> => async (dispatch, getState) => {
  try {
    // CASHDAP-3292: "Not on this device" must stop silent auto-login on this device, not just future
    // mints. Until the consumer revoke endpoint (CASHDAP-3258) can cancel the already-minted cookie
    // server-side, honour the opt-out here by skipping the resume — the user falls through to
    // standard login. Only the pure COOKIE resume is gated: checkPersistentLogin also authenticates
    // token-backed sessions (in-app x-afterpay-colt / x-cash-token, trusted-checkout OAuth headers),
    // which carry their own explicit credentials and must not be blocked by a remember-me opt-out.
    // The marker is only ever set from the ARM issuance toast (which only shows after an ARM mint on
    // this device), so it cannot affect devices that never opted in.
    const state = getState()
    // OAuth presence must check header VALUES: getOAuthLoginHeadersFromOrder sets keys (e.g.
    // oauth-login-time) even when the merchant custom payload carries no OAuth login values, so a
    // key-count check would treat any checkout with unrelated custom data as token-backed.
    //
    // Deliberate fail-closed trade-off: the transaction-token Authorization header is NOT counted —
    // it exists on every checkout, so counting it would make this gate never fire. A trusted resume
    // backed only by the order token (no in-app token, no OAuth values) therefore falls through to
    // standard login on an opted-out device. That errs toward an explicit login — never silent
    // auth — on a device whose user tapped "Not on this device", and never blocks checkout. The
    // whole client-side gate goes away once CASHDAP-3258 revokes the cookie server-side at opt-out.
    const hasTokenBackedCredentials =
      afterpayCheckoutLoginTokenSelector(state) != null ||
      cashCheckoutTokenSelector(state) != null ||
      Object.values(oAuth2faHeaders(state)).some((value) => value != null)
    if (!hasTokenBackedCredentials && hasOptedOutRememberMe()) {
      return null
    }
    const { active, verify } = unwrapResult(await dispatch(checkPersistentLogin()))
    if (active && verify) {
      return Route.VERIFY
    }
    if (active) {
      return unwrapResult(await dispatch(accountLookup()))
    }
  } catch (e) {
    // Intentionally swallow errors as we return null to indicate no persistent login
  }
  return null
}

export const handlePersistentLogin = async (
  checkout: Checkout,
  dispatch: CheckoutAppDispatch
): Promise<InitialiseNextRoute | CrossRegionRedirection | { route: Route } | null> => {
  const account = await dispatch(loadPersistentConsumer())

  if (account !== null) {
    // Persistent logged-in user
    if (isConsumerAccountResponse(account)) {
      return await determinePersistentLoginRoute({ account, checkout }, dispatch)
    } else {
      // trusted checkout 2fa
      return { route: account as Route.VERIFY }
    }
  }

  return null
}
